import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

// Same toolbar as the typing-center pages: one 32px row.
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};
const MENU_ITEM_SX = { fontSize: '0.8125rem' };

// Helper to get field from snake_case or camelCase
const getField = (obj, ...keys) => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return null;
};

// Customer-payment chip. Reads CustomerPaymentStatus values
// (UNPAID / PARTIAL / PAID) directly from the invoice row.
const PAYMENT_CONFIG = {
  PAID: { color: 'success', label: 'Paid' },
  PARTIAL: { color: 'info', label: 'Partial' },
  UNPAID: { color: 'warning', label: 'Unpaid' }
};

const paymentStatusOf = (inv) => getField(inv, 'customerPaymentStatus', 'customer_payment_status') || 'UNPAID';

const amountDueOf = (inv) => {
  const grandTotal = getField(inv, 'grandTotal', 'grand_total') || 0;
  const paidAmount = getField(inv, 'paidAmount', 'paid_amount') || 0;
  return parseFloat(getField(inv, 'amountDue', 'amount_due') ?? (parseFloat(grandTotal) - parseFloat(paidAmount))) || 0;
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED'
}).format(parseFloat(amount) || 0);

const PortalInvoices = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getPortalInvoices();
      setInvoices(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const stats = useMemo(() => {
    const by = (s) => invoices.filter((inv) => paymentStatusOf(inv) === s).length;
    const outstanding = invoices
      .filter((inv) => inv.status !== 'DRAFT' && inv.status !== 'VOID')
      .reduce((sum, inv) => sum + Math.max(amountDueOf(inv), 0), 0);
    return { total: invoices.length, UNPAID: by('UNPAID'), PARTIAL: by('PARTIAL'), PAID: by('PAID'), outstanding };
  }, [invoices]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (statusFilter && inv.status !== statusFilter) return false;
      if (paymentFilter && paymentStatusOf(inv) !== paymentFilter) return false;
      if (!term) return true;
      return (getField(inv, 'invoiceNumber', 'invoice_number') || '').toLowerCase().includes(term);
    });
  }, [invoices, searchTerm, statusFilter, paymentFilter]);

  useEffect(() => { setPage(0); }, [searchTerm, statusFilter, paymentFilter]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const open = (inv) => navigate(`/portal/invoices/${inv.id}`);

  const handleDownloadPdf = async (e, invoiceId, invoiceNumber) => {
    e.stopPropagation();
    setDownloadingPdf(invoiceId);

    try {
      const response = await ApiService.getPortalInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(null);
    }
  };

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button size="small" onClick={fetchInvoices} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <ProTableStats
        loading={loading}
        cards={[
          ...[
            { key: '', label: 'Total', value: stats.total },
            { key: 'UNPAID', label: 'Unpaid', value: stats.UNPAID, color: 'warning.main' },
            { key: 'PARTIAL', label: 'Partly Paid', value: stats.PARTIAL, color: 'info.main' },
            { key: 'PAID', label: 'Paid', value: stats.PAID, color: 'success.main' },
          ].map((c) => ({
            ...c,
            highlight: paymentFilter === c.key,
            onClick: () => setPaymentFilter(c.key),
          })),
          { key: 'outstanding', label: 'Outstanding', value: formatCurrency(stats.outstanding), color: stats.outstanding > 0 ? 'error.main' : 'success.main' },
        ]}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <TextField
          size="small"
          placeholder="Search by invoice number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="" sx={MENU_ITEM_SX}>All</MenuItem>
            <MenuItem value="DRAFT" sx={MENU_ITEM_SX}>Draft</MenuItem>
            <MenuItem value="FINAL" sx={MENU_ITEM_SX}>Issued</MenuItem>
            <MenuItem value="PAID" sx={MENU_ITEM_SX}>Paid</MenuItem>
            <MenuItem value="VOID" sx={MENU_ITEM_SX}>Void</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Payment</InputLabel>
          <Select value={paymentFilter} label="Payment" onChange={(e) => setPaymentFilter(e.target.value)}>
            <MenuItem value="" sx={MENU_ITEM_SX}>All</MenuItem>
            <MenuItem value="UNPAID" sx={MENU_ITEM_SX}>Unpaid</MenuItem>
            <MenuItem value="PARTIAL" sx={MENU_ITEM_SX}>Partly Paid</MenuItem>
            <MenuItem value="PAID" sx={MENU_ITEM_SX}>Paid</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {filtered.length} invoices
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchInvoices} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance Due</TableCell>
                <TableCell align="center">Payment</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {invoices.length === 0 ? 'No invoices yet' : 'No matching invoices'}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((inv) => {
                  const invoiceNumber = getField(inv, 'invoiceNumber', 'invoice_number');
                  const workOrderNumber = getField(inv, 'workOrderNumber', 'work_order_number');
                  const invoiceDate = getField(inv, 'invoiceDate', 'invoice_date');
                  const dueDate = getField(inv, 'dueDate', 'due_date');
                  const grandTotal = getField(inv, 'grandTotal', 'grand_total') || 0;
                  const paidAmount = parseFloat(getField(inv, 'paidAmount', 'paid_amount') || 0);
                  const amountDue = amountDueOf(inv);
                  const payment = PAYMENT_CONFIG[paymentStatusOf(inv)] || PAYMENT_CONFIG.UNPAID;

                  return (
                    <TableRow key={inv.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(inv)}>
                      <TableCell>
                        <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{invoiceNumber || '—'}</Box>
                        {workOrderNumber && (
                          <Box sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>WO: {workOrderNumber}</Box>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(invoiceDate, '—')}</TableCell>
                      <TableCell>{formatDate(dueDate, '—')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(grandTotal)}</TableCell>
                      <TableCell align="right" sx={{ color: paidAmount > 0 ? 'success.main' : 'text.secondary' }}>
                        {formatCurrency(paidAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: amountDue > 0 ? 'warning.main' : 'success.main' }}>
                        {formatCurrency(amountDue)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={payment.label} size="small" color={payment.color} />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="View Invoice">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); open(inv); }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {inv.status !== 'DRAFT' && (
                          <Tooltip title="Download PDF">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => handleDownloadPdf(e, inv.id, invoiceNumber)}
                                disabled={downloadingPdf === inv.id}
                              >
                                {downloadingPdf === inv.id
                                  ? <CircularProgress size={16} />
                                  : <PdfIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default PortalInvoices;
